import React from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import IsoWeek from "dayjs/plugin/isoWeek";
import { SnackbarProvider } from "notistack";
import { GlobalStyles } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { ConfirmProvider } from "material-ui-confirm";
import { Provider as ReduxProvider } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { useJsApiLoader, LoadScriptProps } from "@react-google-maps/api";

import { store } from "store";
import {
  AuthRoute,
  CommonLayout,
  ProtectedRoute,
  ComponentLoader,
  TopBarNavigation,
} from "common";

import { routes } from "./routes";
import { adminTheme, iframeTheme, StyledSnackbarComponent } from "./theme";

const IFrame = React.lazy(() => import("pages/user-flow/i-frame"));
const IFrameDev = React.lazy(() => import("pages/user-flow/i-frame-dev"));

const RoutesList = React.lazy(() => import("pages/user-flow/routes-list"));
const RoutesListDev = React.lazy(
  () => import("pages/user-flow/routes-list-dev")
);

dayjs.extend(IsoWeek);

const AppContainer = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const libraries: LoadScriptProps["libraries"] = ["places"];

const App = () => {
  const { isLoaded } = useJsApiLoader({
    libraries,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
  });

  if (!isLoaded) {
    return null;
  }

  return (
    <ReduxProvider store={store}>
      <React.Suspense fallback={<ComponentLoader loading />}>
        <SnackbarProvider
          maxSnack={3}
          Components={{
            success: StyledSnackbarComponent,
            error: StyledSnackbarComponent,
          }}
        >
          <ConfirmProvider>
            <BrowserRouter>
              <Routes>
                {routes.auth.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <AppContainer
                        style={{
                          background: "radial-gradient(#222222, #111111)",
                        }}
                      >
                        <ThemeProvider theme={adminTheme}>
                          <CssBaseline />
                          <AuthRoute>{route.element}</AuthRoute>
                        </ThemeProvider>
                      </AppContainer>
                    }
                  />
                ))}

                <Route
                  path="/iframe"
                  element={
                    <AppContainer style={{ background: "transparent" }}>
                      <ThemeProvider theme={iframeTheme}>
                        <CssBaseline />
                        <GlobalStyles
                          styles={{
                            body: { backgroundColor: "transparent" },
                          }}
                        />
                        <IFrame isPresentFromModal />
                      </ThemeProvider>
                    </AppContainer>
                  }
                />

                <Route
                  path="/iframe-section"
                  element={
                    <AppContainer style={{ background: "transparent" }}>
                      <ThemeProvider theme={iframeTheme}>
                        <CssBaseline />
                        <GlobalStyles
                          styles={{
                            body: { backgroundColor: "transparent" },
                          }}
                        />
                        <IFrame isPresentFromModal={false} />
                      </ThemeProvider>
                    </AppContainer>
                  }
                />

                <Route
                  path="/iframe-dev"
                  element={
                    <AppContainer
                      style={{
                        background: "radial-gradient(#222222, #111111)",
                      }}
                    >
                      <ThemeProvider theme={iframeTheme}>
                        <CssBaseline />
                        <GlobalStyles
                          styles={{ body: { overflowY: "auto" } }}
                        />
                        <IFrameDev />
                      </ThemeProvider>
                    </AppContainer>
                  }
                />

                <Route
                  path="/routes-list"
                  element={
                    <AppContainer style={{ background: "transparent" }}>
                      <ThemeProvider theme={iframeTheme}>
                        <CssBaseline />
                        <GlobalStyles
                          styles={{
                            body: { backgroundColor: "transparent" },
                          }}
                        />

                        <RoutesList />
                      </ThemeProvider>
                    </AppContainer>
                  }
                />

                <Route
                  path="/routes-list-dev"
                  element={
                    <AppContainer
                      style={{
                        background: "radial-gradient(#222222, #111111)",
                      }}
                    >
                      <ThemeProvider theme={iframeTheme}>
                        <CssBaseline />
                        <RoutesListDev />
                      </ThemeProvider>
                    </AppContainer>
                  }
                />

                <Route
                  path="*"
                  element={
                    <AppContainer
                      style={{
                        background: "radial-gradient(#222222, #111111)",
                      }}
                    >
                      <ThemeProvider theme={adminTheme}>
                        <CssBaseline />
                        <ProtectedRoute>
                          <TopBarNavigation />

                          <CommonLayout loading={!isLoaded}>
                            <Routes>
                              {routes.private.map((route) => (
                                <Route
                                  element={route.element}
                                  path={`${route.path}/*`}
                                  key={`route-${route.path}`}
                                />
                              ))}
                            </Routes>
                          </CommonLayout>
                        </ProtectedRoute>
                      </ThemeProvider>
                    </AppContainer>
                  }
                />
              </Routes>
            </BrowserRouter>
          </ConfirmProvider>
        </SnackbarProvider>
      </React.Suspense>
    </ReduxProvider>
  );
};

export default App;
